import React from "react";
import {
    EquipmentDetailsDirect,
    SystemDesignDetailsDirect,
} from "../../../../../services/oneButtonServices/opportunityValidationService";
import {Collapse, Flex, Table, Typography} from "antd";
import {failureIcon, groupBy, successIcon} from "./OpportunityValidationShared";

interface EquipmentDetailsProps {
    systemDesign: SystemDesignDetailsDirect[];
}

function normalizeSourceKey(source: string | null | undefined, idx: number): string {
    const trimmed = String(source ?? '').trim();
    return trimmed || `Unknown-${idx}`;
}

function renderEquipmentCell(value: unknown) {
    if (value == null || value === '') {
        return null;
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    return null;
}

export const EquipmentDetails: React.FC<EquipmentDetailsProps> = ({systemDesign}) => {

    const flatArrays = systemDesign
        .filter(s => s.EquipmentDetails != null)
        .flatMap((s, idx) =>
            s.EquipmentDetails!.map((e) => ({
                source: normalizeSourceKey(s.Source, idx),
                array: e,
            })));

    const arrays = groupBy(flatArrays, a => a.array.ArrayNumber);

    const columns = [
        {
            key: 'title',
            dataIndex: 'title',
            width: 240,
            render: (value: unknown) => (
                <span className="obd-oppval-equipment-uititle">{renderEquipmentCell(value) ?? ''}</span>
            ),
        },
        ...buildSourceColumns(systemDesign),
        {
            key: 'status',
            title: 'Status',
            dataIndex: 'status',
            align: 'center' as const,
            width: 90,
            render: (value: unknown) => (React.isValidElement(value) ? value : renderEquipmentCell(value)),
        },
    ];

    return (
        <Flex vertical>
            <Table
                size={'small'}
                pagination={false}
                tableLayout="fixed"
                rowKey="title"
                columns={columns}
                dataSource={buildItems(
                    [
                        {
                            uiTitle: 'System Size',
                            apiField: 'SystemSize',
                        },
                        {
                            uiTitle: 'Production',
                            apiField: 'Production',
                        },
                        {
                            uiTitle: 'Yearly Saving',
                            apiField: 'YearlySaving',
                        },
                        {
                            uiTitle: 'EPC',
                            apiField: 'EPC',
                        },
                    ],
                    systemDesign)}/>

            <div className="obd-oppval-section-title" style={{marginTop: 12, marginBottom: 6}}>
                <Typography.Text strong>Packing Slip</Typography.Text>
            </div>
            <Table
                size={'small'}
                pagination={false}
                tableLayout="fixed"
                rowKey="title"
                columns={columns}
                dataSource={buildItems(
                    [
                        {
                            uiTitle: 'Module Manufacturer',
                            apiField: 'ModuleManufacturer',
                        },
                        {
                            uiTitle: 'Module Model',
                            apiField: 'ModuleModel',
                        },
                        {
                            uiTitle: 'Module Quantity',
                            apiField: 'ModuleQuantity',
                        },
                        {
                            uiTitle: 'Inverter Manufacturer',
                            apiField: 'InverterManufacturer',
                        },
                        {
                            uiTitle: 'Inverter Model',
                            apiField: 'InverterModel',
                        },
                        {
                            uiTitle: 'Inverter Quantity',
                            apiField: 'InverterQuantity',
                        },
                        {
                            uiTitle: 'Optimizer Manufacturer',
                            apiField: 'OptimizerManufacturer',
                        },
                        {
                            uiTitle: 'Optimizer',
                            apiField: 'OptimizerModel',
                        },
                        {
                            uiTitle: 'Optimizer Quantity',
                            apiField: 'OptimizerQuantity',
                        },
                        {
                            uiTitle: 'Racking Manufacturer',
                            apiField: 'RackingManufacturer',
                        },
                        {
                            uiTitle: 'Racking Model',
                            apiField: 'RackingModel',
                        },
                        {
                            uiTitle: 'Racking Parts',
                            apiField: 'RackingParts',
                        },
                        {
                            uiTitle: 'Racking Type',
                            apiField: 'RackingType',
                        },
                    ],
                    systemDesign)}/>

            <div className="obd-oppval-section-title" style={{marginTop: 12, marginBottom: 6}}>
                <Typography.Text strong>Production Values</Typography.Text>
            </div>
            <Collapse
                items={Object.keys(arrays).map(g => {
                        return {
                            key: g,
                            label: g,
                            children: <Table
                                size={'small'}
                                pagination={false}
                                tableLayout="fixed"
                                rowKey="title"
                                columns={columns}
                                dataSource={buildArrayItems(
                                    [
                                        {
                                            uiTitle: 'Tilt',
                                            apiField: 'Tilt',
                                        },
                                        {
                                            uiTitle: 'Azimuth',
                                            apiField: 'Azimuth',
                                        },
                                        {
                                            uiTitle: 'Solar Availability',
                                            apiField: 'SolarAvailability',
                                        },
                                        {
                                            uiTitle: 'Building Type',
                                            apiField: 'BuildingType',
                                        },
                                        {
                                            uiTitle: 'Module Quantity',
                                            apiField: 'ModuleQuantity',
                                        },
                                        {
                                            uiTitle: 'Module Wattage',
                                            apiField: 'ModuleWattage',
                                        },
                                    ],
                                    arrays[g])}/>
                        };
                    }
                )}/>
        </Flex>
    );
};

function buildSourceColumns(systemDesign: SystemDesignDetailsDirect[]) {
    return systemDesign.map((s, idx) => {
        const sourceKey = normalizeSourceKey(s.Source, idx);
        const isSfdc = sourceKey.toLowerCase() === 'sfdc';

        return {
            title: resolveColumnTitle(s),
            dataIndex: sourceKey,
            key: sourceKey,
            width: 220,
            render: (value: unknown, record: Record<string, unknown>) => {
                const isEpcRow = String(record?.title ?? '').trim() === 'EPC';
                const isFailed = record?.status === failureIcon;
                const shouldHighlight = isSfdc && isEpcRow && isFailed;
                const display = renderEquipmentCell(value);

                return (
                    <span style={shouldHighlight ? {color: '#ef4444', fontWeight: 600} : undefined}>
                        {display}
                    </span>
                );
            },
        };
    });
}

function resolveColumnTitle(systemDesign: SystemDesignDetailsDirect) {
    switch (systemDesign.Source) {
        case 'TPO':
            return systemDesign.FinacePartner;
        default:
            return systemDesign.Source;
    }
}

type Pair = {
    uiTitle: string;
    apiField: string;
}

function buildItems(pairs: Pair[], systemDesign: SystemDesignDetailsDirect[]) {
    return pairs.map(pair => {
        const column: Record<string, any> = {
            title: pair.uiTitle,
        };

        const rawValues: string[] = [];

        systemDesign.forEach((s, idx) => {
            const sourceKey = normalizeSourceKey(s.Source, idx);
            const value = String((s as Record<string, unknown>)[pair.apiField] ?? '');

            rawValues.push(value);
            column[sourceKey] = value;
        });

        column.status = getStatusIcon(pair.uiTitle, rawValues);

        return column;
    });
}

function buildArrayItems(pairs: Pair[], arrays: { source: string, array: EquipmentDetailsDirect }[]) {

    return pairs.map(pair => {
        const column: Record<string, any> = {
            title: pair.uiTitle,
        };

        const rawValues: string[] = [];

        arrays.forEach(a => {
            const value = String((a.array as any)[pair.apiField] ?? '');

            rawValues.push(value);
            column[a.source] = value;
        });

        column.status = getStatusIcon(pair.uiTitle, rawValues);

        return column;
    });
}

function getStatusIcon(fieldLabel: string, rawValues: string[]) {
    // Ensure we have an array to work with
    if (!Array.isArray(rawValues) || rawValues.length === 0) {
        return '';
    }

    const normalize = (v: any): string => {
        if (v === null || v === undefined) {
            return '';
        }
        let trimmed = String(v).trim();
        if (!trimmed) {
            return '';
        }

        // --- Added for Module Model: Normalize "+" and "plus" ---
        if (fieldLabel === 'Module Model' || fieldLabel === 'Module Manufacturer') {

            // 1. Normalize embedded patterns like G10+410, G10plus410, Cplus410
            trimmed = trimmed.replace(/([A-Za-z0-9])\s*(?:\+|plus)\s*(\d+)/gi, '$1 plus $2');

            // 2. Replace standalone "+" → " plus "
            trimmed = trimmed.replace(/\+/g, ' plus ');

            // 3. Normalize all "plus" variants → single " plus "
            trimmed = trimmed.replace(/\bplus\b/gi, ' plus ');

            // 4. Replace special symbols _ - . with space
            trimmed = trimmed.replace(/[_\-.]/g, ' ');

            // 5. Collapse multiple spaces & trim
            trimmed = trimmed.replace(/\s+/g, ' ').trim();
        }

        // Normalize 'na' (case-insensitive) to a canonical 'NA' marker,
        // and normalize other values to lower-case for consistent comparisons.
        return trimmed.toLowerCase() === 'na' ? 'NA' : trimmed.toLowerCase();
    };

    const values = rawValues.map((value) => normalize(value));
    // comparable contains non-NA, non-empty values for equality checks
    const comparable = values.filter((value) => value !== 'NA' && value !== '');
    const allEmpty = values.every((value) => value === '');
    const allNA = values.every((value) => value === 'NA');
    let allEqualNonNA = comparable.length > 0 && comparable.every((value) => value === comparable[0]);


    // Handle Inverter Manufacturer and Inverter Model
    if (fieldLabel === 'Inverter Manufacturer' || fieldLabel === 'Inverter Model') {
        const cleanToArray = (val: string): string[] => {
            return String(val ?? '')
                .replace(/\([^)]*\)/g, '')     // remove (1), (2,3)
                .trim()
                .toLowerCase()
                .split(',')
                .map(v => v.trim())
                .filter(Boolean);
        };

        // BASE = values[1]
        const baseArr = [...new Set(cleanToArray(values[1]))];  // unique base
        let allMatch = true;

        // Compare values[1] with all others
        comparable.forEach((val) => {

            const compareArr = [...new Set(cleanToArray(val))];
            const matches = baseArr.every(item => compareArr.includes(item));

            if (!matches) allMatch = false;
        });
        allEqualNonNA = allMatch;
    }

    // Handle ModuleManufacturer: mark as matching when values include Hanwha or HANWHA_Q_CELLS (for TPO)
    if (fieldLabel === 'Module Manufacturer' && values[1] === 'hanwha') {
        const hasMatch = comparable.length > 0 && comparable.every(v => {
            const val = String(v || '').toLowerCase();
            return val.includes('hanwha') || val.includes('hanwha_q_cells');
        });
        allEqualNonNA = hasMatch;
    }

    // Handle Optimizer: generalized matching using base models
    if (fieldLabel === 'Optimizer') {
        const baseModels = [
            'u650-1gm4mrmu', // covers 'u650-1gm4mrmu - domestic'
            'p485',          // covers 'p485-4nm4mrm'
            's440',          // covers 's440-1gm4mrm'
            's500b',         // covers 's500b-1gm4mrm'
            'p340',          // covers 'solar edge p340 - tsh'
            'p401',          // covers 'solar edge p401'
            'u650b-1gm4mbmu' // covers 'u650b-1gm4mbmu-a25'
        ];

        // values[1] is oneBUTTON source
        const oneButtonVal = String(values[1] || '').toLowerCase();
        const matchedModel = baseModels.find(m => oneButtonVal.includes(m));

        if (matchedModel) {
            // Mark as equal if all non-NA values contain the matched base model
            const hasMatch = comparable.length > 0 && comparable.every(v =>
                String(v || '').toLowerCase().includes(matchedModel)
            );
            allEqualNonNA = hasMatch;
        }
    }

    if (allEmpty) {
        return '';
    }

    // Use indexOf instead of includes to avoid depending on newer lib targets
    const hasEmpty = values.indexOf('') !== -1;
    const showSuccess = (allEqualNonNA || allNA) && !hasEmpty;

    return showSuccess
        ? successIcon
        : failureIcon;
}